import BigNumber from 'bignumber.js';

export function getInteger(value: string): string {
  const decPos = value.indexOf('.');
  const substrLength = decPos === -1 ? value.length : decPos;
  return value.substring(0, substrLength);
}

export function getDecimal(locale: string, options?: Intl.NumberFormatOptions) {
  return (
    new Intl.NumberFormat(locale, options)
      .formatToParts(0.1)
      .filter((part: { type: string; value: string }) => part.type === 'decimal')[0]?.value || ''
  );
}

export function getFraction(value: string) {
  const decPos = value.indexOf('.');
  const substrLength = decPos === -1 ? value.length : decPos;
  return value.substring(substrLength);
}

export function getCleanedValue(
  value: string,
  decimal: string,
  maximumFractionDigits: number
): string {
  const regex = new RegExp(`[^-\\d${decimal}]`, 'g');
  value = value.trim().replace(regex, '');
  if (decimal) {
    const decimalString = new RegExp(`\\${decimal}`, 'g');
    value = value.replace(decimalString, '.');
  }
  if (value.startsWith('.')) value = `0${value}`;
  if (value.startsWith('-.')) value = `-0${value.substring(1)}`;
  const match = value.match(new RegExp(`-?\\d*(\\.\\d{0,${maximumFractionDigits}})?`));
  if (match && match.length > 0) {
    value = match[0];
  }
  return value;
}

export function getFormattedValue(
  value: string,
  locale: string,
  showGroupSeparators: boolean,
  maximumFractionDigits: number = 0
): string {
  let valueNumber;
  let sign = '';
  const cleanedValue = getCleanedValue(value, '.', maximumFractionDigits);
  let integer = getInteger(cleanedValue);
  const decimal = getDecimal(locale);
  const fractionPart = getFraction(cleanedValue).replace('.', decimal);
  if (integer && integer.startsWith('-')) {
    sign = '-';
    integer = integer.substring(1);
  }
  if (!integer && maximumFractionDigits && fractionPart.startsWith(decimal)) {
    integer = '0';
  }
  if (cleanedValue && !Number.isNaN(parseFloat(integer))) {
    valueNumber = BigInt(integer);
    return (
      sign +
      new Intl.NumberFormat(locale, { useGrouping: showGroupSeparators })
        .format(valueNumber)
        .trim() +
      (maximumFractionDigits ? fractionPart : '')
    );
  }
  return cleanedValue;
}

export function isValueInRange(
  value: number,
  min: number | undefined,
  max: number | undefined
): boolean {
  return !((min !== undefined && value < min) || (max !== undefined && value > max));
}

export function getIncrementedValue(value: string, min: number, max: number, step: number): string {
  const numberValue = parseFloat(value) || 0;
  const newValue = new BigNumber(numberValue).plus(step);
  if (isValueInRange(newValue.toNumber(), min, max)) {
    return newValue.toFixed();
  }
  if (min !== undefined && newValue.toNumber() < min) {
    return min.toString();
  }
  return value;
}

export function getDecrementedValue(value: string, min: number, max: number, step: number): string {
  const numberValue = parseFloat(value) || 0;
  const newValue = new BigNumber(numberValue).minus(step);
  if (isValueInRange(newValue.toNumber(), min, max)) {
    return newValue.toFixed();
  }
  if (max !== undefined && newValue.toNumber() > max) {
    return max.toString();
  }
  return value;
}
